import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Loader2,
  Trophy,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail
} from "../../../Config/matuAuth";
import { ref, set, get, serverTimestamp } from "../../../Config/matuRtdb";
import { auth, database } from '../../../Config/firebase';

const InputField = ({
  label,
  type,
  placeholder,
  value,
  onChange,
  icon,
  showPassword,
  onTogglePassword,
  error,
}) => {
  return (
    <div className="relative mb-4">
      <label className="block text-white/90 font-medium mb-1.5 text-sm">
        {label}
      </label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          {icon}
        </div>
        <input
          type={type === "password" && showPassword ? "text" : type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full pl-10 pr-12 py-3.5 bg-white/8 backdrop-blur-lg border rounded-xl
                     text-white placeholder-gray-400 font-medium text-base
                     transition-all duration-200 ease-out
                     focus:outline-none focus:border-green-400 focus:bg-white/12
                     hover:bg-white/10 hover:border-white/30
                     ${error ? "border-red-400/70" : "border-white/20"}
                     shadow-sm`}
          autoComplete={type === "email" ? "email" : type === "password" ? "current-password" : "off"}
        />
        {type === "password" && (
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-150 p-1"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      {error && (
        <p className="text-red-400 text-xs mt-1.5 ml-0.5 flex items-center gap-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
};


const LoginButton = ({ onClick, isLoading }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isLoading}
    className="w-full py-3.5 px-4 rounded-xl font-semibold text-base
               bg-gradient-to-r from-green-500 to-teal-500 text-white
               hover:from-green-600 hover:to-teal-600
               transform transition-all duration-200 ease-out
               hover:scale-[1.02] active:scale-[0.98] 
               disabled:opacity-60 disabled:hover:scale-100
               shadow-lg hover:shadow-xl
               flex items-center justify-center gap-2"
  >
    {isLoading ? (
      <>
        <Loader2 size={18} className="animate-spin" />
        Iniciando sesión...
      </>
    ) : (
      "Iniciar Sesión"
    )}
  </button>
);

const Notification = ({ type, message, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg border backdrop-blur-lg transform transition-all duration-500 ease-out
                   ${type === 'success' 
                     ? 'bg-green-500/10 border-green-400/30 text-green-200' 
                     : 'bg-red-500/10 border-red-400/30 text-red-200'
                   }`}>
    <div className="flex items-center gap-2">
      {type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
      <span className="text-sm font-medium">{message}</span>
      <button 
        onClick={onClose}
        className="ml-2 text-gray-400 hover:text-white transition-colors"
      >
        ×
      </button>
    </div>
  </div>
);

export function LoginPages() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  const navigate = useNavigate();
  const googleProvider = new GoogleAuthProvider();

  // Función para guardar UID en localStorage
  const saveUserToLocalStorage = (uid) => {
    try {
      localStorage.setItem('userUID', uid);
    } catch (error) {
      console.error('Error saving UID to localStorage:', error);
    }
  };

  // Función para obtener UID de localStorage
  const getUserFromLocalStorage = () => {
    try {
      return localStorage.getItem('userUID');
    } catch (error) {
      console.error('Error getting UID from localStorage:', error);
      return null;
    }
  };

  // Función para limpiar localStorage
  const clearUserFromLocalStorage = () => {
    try {
      localStorage.removeItem('userUID');
    } catch (error) {
      console.error('Error clearing UID from localStorage:', error);
    }
  };

  // Verificar si hay usuario guardado al montar el componente
  useEffect(() => {
    const savedUID = getUserFromLocalStorage();

    if (savedUID) {
      // Si hay UID guardado, mostrar splash de marca y redirigir rápido
      showNotification('success', 'Sesión restaurada. Redirigiendo...');
      const t = setTimeout(() => navigate('/gta-app/home'), 900);
      return () => clearTimeout(t);
    } else {
      // Si no hay UID, mostrar el formulario
      setIsCheckingAuth(false);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [navigate]);

  // Listener para cambios de autenticación
  useEffect(() => {
    let redirectTimer = null;
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        saveUserToLocalStorage(currentUser.uid);
        showNotification('success', '¡Bienvenido!');
        redirectTimer = setTimeout(() => {
          navigate('/gta-app/home');
        }, 700);
      } else {
        clearUserFromLocalStorage();
        setIsCheckingAuth(false);
      }
    });

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
      unsubscribe();
    };
  }, [navigate]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = "Email requerido";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email inválido";
    }

    if (!password) {
      newErrors.password = "Contraseña requerida";
    } else if (password.length < 6) {
      newErrors.password = "Mínimo 6 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Función para actualizar datos del usuario en Realtime Database
  const updateUserDatabase = async (user, additionalData = {}) => {
    try {
      const userRef = ref(database, `users/${user.uid}`);
      
      // Verificar si el usuario ya existe
      const snapshot = await get(userRef);
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email.split('@')[0],
        photoURL: user.photoURL || null,
        lastLogin: serverTimestamp(),
        ...additionalData
      };

      if (!snapshot.exists()) {
        // Usuario nuevo, agregar fecha de creación
        userData.createdAt = serverTimestamp();
        userData.isNewUser = true;
      } else {
        // Usuario existente, mantener datos originales
        const existingData = snapshot.val();
        userData.createdAt = existingData.createdAt;
        userData.isNewUser = false;
      }

      await set(userRef, userData);
      return userData;
    } catch (error) {
      console.error('Error updating user database:', error);
      throw error;
    }
  };

  const handleEmailLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Actualizar datos en Realtime Database
      await updateUserDatabase(user, {
        loginMethod: 'email'
      });

      // Guardar UID en localStorage
      saveUserToLocalStorage(user.uid);

      showNotification('success', '¡Inicio de sesión exitoso!');
      
      // El redirect se maneja en el useEffect del onAuthStateChanged
      
    } catch (error) {
      let errorMessage = 'Error al iniciar sesión';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No existe una cuenta con este email';
          setErrors({ email: errorMessage });
          break;
        case 'auth/wrong-password':
          errorMessage = 'Contraseña incorrecta';
          setErrors({ password: errorMessage });
          break;
        case 'auth/invalid-email':
          errorMessage = 'Email inválido';
          setErrors({ email: errorMessage });
          break;
        case 'auth/user-disabled':
          errorMessage = 'Esta cuenta ha sido deshabilitada';
          setErrors({ general: errorMessage });
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Demasiados intentos fallidos. Inténtalo más tarde';
          setErrors({ general: errorMessage });
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Error de conexión. Verifica tu internet';
          setErrors({ general: errorMessage });
          break;
        default:
          setErrors({ general: errorMessage });
      }
      
      showNotification('error', errorMessage);
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setErrors({ email: 'Ingresa tu email para recuperar la contraseña' });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: 'Email inválido' });
      return;
    }

    setIsResetLoading(true);
    setErrors({});

    try {
      await sendPasswordResetEmail(auth, email);
      showNotification('success', 'Email de recuperación enviado. Revisa tu bandeja de entrada');
    } catch (error) {
      let errorMessage = 'Error al enviar email de recuperación';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No existe una cuenta con este email';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Email inválido';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Demasiados intentos. Inténtalo más tarde';
          break;
        default:
          console.error('Password reset error:', error);
      }
      
      showNotification('error', errorMessage);
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const goToRegister = () => {
    navigate('/gta-app/register');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleEmailLogin();
    }
  };

  // Mostrar loading mientras verifica autenticación
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-blue-900 to-teal-900 flex items-center justify-center">
        <div className="text-center">
          <div className="p-4 rounded-2xl bg-gradient-to-r from-green-500 to-teal-500 shadow-lg mb-4 inline-block">
            <Trophy size={32} className="text-white" />
          </div>
          <div className="flex items-center justify-center gap-2 text-white">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-lg font-medium">Verificando sesión...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-green-900 via-blue-900 to-teal-900">
      {/* Notificación */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      
      {/* Subtle animated background */}
      <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/40 via-green-800/20 to-transparent">
        <div className="absolute inset-0 opacity-10">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-pulse text-green-400/30"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            >
              <Trophy size={16 + Math.random() * 8} />
            </div>
          ))}
        </div>
      </div>

      {/* Back button */}
      <button
        onClick={handleBack}
        className="fixed top-4 left-4 z-50 p-2.5 rounded-full bg-black/20 backdrop-blur-lg border border-white/10
                   text-white hover:bg-black/30 transition-all duration-200 ease-out
                   hover:scale-105 active:scale-95 shadow-sm"
      >
        <ArrowLeft size={20} />
      </button>

      {/* Login form */}
      <div
        className={`relative z-10 min-h-screen flex items-center justify-center px-4 py-8
                      transform transition-all duration-700 ease-out
                      ${
                        isVisible
                          ? "translate-y-0 opacity-100"
                          : "translate-y-4 opacity-0"
                      }`}
      >
        <div className="w-full max-w-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-3">
              <div className="p-3 rounded-2xl bg-gradient-to-r from-green-500 to-teal-500 shadow-lg">
                <Trophy size={24} className="text-white" />
              </div>
            </div>
            <h1
              className="text-3xl font-bold mb-2
                          bg-gradient-to-r from-green-300 via-teal-200 to-blue-300 bg-clip-text text-transparent
                          drop-shadow-sm"
            >
              SportConnect
            </h1>
            <p className="text-base text-gray-300 font-medium">
              Bienvenido de vuelta
            </p>
          </div>

          {/* Form */}
          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
              {errors.general && (
                <div className="mb-4 p-3 bg-red-500/15 border border-red-400/30 rounded-lg">
                  <p className="text-red-200 text-sm text-center flex items-center justify-center gap-2">
                    <AlertCircle size={16} />
                    {errors.general}
                  </p>
                </div>
              )}

              {/* Email field */}
              <InputField
                label="Email"
                type="email"
                placeholder="tu@ejemplo.com"
                value={email}
                onChange={setEmail}
                icon={<Mail size={18} />}
                error={errors.email}
              />

              {/* Password field */}
              <div onKeyPress={handleKeyPress}>
                <InputField
                  label="Contraseña"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={setPassword}
                  icon={<Lock size={18} />}
                  showPassword={showPassword}
                  onTogglePassword={() => setShowPassword(!showPassword)}
                  error={errors.password}
                />
              </div>

              {/* Login button */}
              <div className="mt-6">
                <LoginButton onClick={handleEmailLogin} isLoading={isLoading} />
              </div>
            </div>
          </div>

          {/* Footer links */}
          <div className="text-center mt-6 space-y-3">
            <button 
              onClick={handlePasswordReset}
              disabled={isResetLoading}
              className="text-gray-400 hover:text-white transition-colors duration-200 text-sm font-medium
                         disabled:opacity-50 flex items-center justify-center gap-1 mx-auto"
            >
              {isResetLoading && <Loader2 size={14} className="animate-spin" />}
              ¿Olvidaste tu contraseña?
            </button>
            <div className="flex items-center justify-center gap-1 text-sm text-gray-400">
              <span>¿No tienes cuenta?</span>
              <button
                onClick={goToRegister}
                className="text-green-400 hover:text-green-300 font-semibold transition-colors duration-200 ml-1"
              >
                Regístrate
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPages;